import { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import GoBoard from './components/GoBoard/GoBoard';
import Review from './components/Review/Review';
import Auth from './components/Auth/Auth';
import { io, Socket } from 'socket.io-client';
import { GoGame, StoneColor, Coordinate, SoundManager } from '@go-platform/go-engine';
import { SERVER_URL, UserInfo, TOKEN_KEY, REMEMBERED_USER_KEY, rankLabel, changeAvatar, changeNickname } from './api';
import { AVATAR_EMOJIS, avatarGlyph, seatRankText } from './avatars';
import './App.css';

/** 对局座位（roomState 本批起为对象）：AI 座 avatar=-1；rank 为 null 时前端降级显示 */
interface SeatInfo {
  nickname: string;
  rank: number | null;
  avatar: number;
}
/** 服务端 roomState 结构 */
interface ClockSide { main: number; byoyomi: number; periods: number; }
interface ClockState {
  current: 'black' | 'white';
  mainTime: number;
  byoyomiTime: number;
  black: ClockSide;
  white: ClockSide;
}
interface RoomState {
  code: string;
  status: 'waiting' | 'playing' | 'finished';
  boardSize: number;
  ai: boolean;
  countMinMoves: number;
  black: SeatInfo | null;
  white: SeatInfo | null;
  spectators: string[];
  currentPlayer: 'black' | 'white' | null;
  board: number[][] | null;
  captureRule: number;
  captures: { black: number; white: number } | null;
  result: { winner: string; reason: string; blackScore?: number; whiteScore?: number; margin?: number } | null;
  moveCount: number;
  moveLog: { x: number | null; y: number | null; color: string }[];
  clock: ClockState | null;
  pendingUndo: boolean;
  pendingDraw: boolean;
  pendingCount: { result: { black: number; white: number; dead: Coordinate[]; territory?: { black: Coordinate[]; white: Coordinate[] }; winner?: string; margin?: number }; from: string } | null;
  rankChanges: { side: 'black' | 'white'; before: number; after: number; beforeLabel: string; afterLabel: string; delta: number; reason: string }[] | null;
  problem: {
    title: string;
    description?: string;
    type?: string;
    practiceOnly?: boolean;
    goal: string;
    canRedo?: boolean;
    marks?: Array<{ coord: Coordinate; type: 'circle' | 'triangle' | 'cross' | 'square' | 'letter'; letter?: string }>;
  } | null;
}

type View = 'lobby' | 'game' | 'review' | 'problems';
type Size = 13 | 19;
type AuthState = 'loading' | 'unauthenticated' | 'authenticated';

const ROOM_KEY = 'go-platform-room-code';
const LAST_LEVEL_KEY = 'go-platform-last-ai-level';
const LAST_COLOR_KEY = 'go-platform-last-ai-color';

function readLastAiLevel(): 'auto' | number {
  try {
    const raw = localStorage.getItem(LAST_LEVEL_KEY);
    if (raw === 'auto') return 'auto';
    const n = Number(raw);
    return Number.isInteger(n) && n >= 10 && n <= 31 ? n : 'auto';
  } catch { return 'auto'; }
}

function readLastAiColor(): 'black' | 'white' {
  try { return localStorage.getItem(LAST_COLOR_KEY) === 'white' ? 'white' : 'black'; } catch { return 'black'; }
}

/** 空昵称或系统临时昵称（棋童1234）需要补一个真昵称 */
function needsNickname(user: UserInfo | null): boolean {
  const n = (user?.nickname || '').trim();
  return !n || /^棋童\d+$/.test(n);
}

const REASON_TEXT: Record<string, string> = {
  counting: '数子',
  resign: '认输',
  draw: '和棋',
  timeout: '超时',
};

/** 对比前后棋盘，返回发生变化的坐标（作为最后一步高亮） */
function diffBoard(prev: number[][] | null, next: number[][] | null): Coordinate | null {
  if (!prev || !next) return null;
  const size = next.length;
  for (let y = 0; y < size; y++) {
    const row = next[y];
    const prevRow = prev[y];
    for (let x = 0; x < row.length; x++) {
      if (!prevRow || prevRow[x] !== row[x]) {
        return { x: x + 1, y: y + 1 };
      }
    }
  }
  return null;
}

/** 统计棋盘上棋子总数（用于提子判定） */
function countStones(board: number[][] | null): number {
  if (!board) return 0;
  let n = 0;
  for (const row of board) for (const c of row) if (c !== 0) n++;
  return n;
}

/** 坐标文本（列 A-T 跳 I，行从上到下递减） */
function coordLabel(c: Coordinate, size: number): string {
  const letters = 'ABCDEFGHJKLMNOPQRST';
  return `${letters[c.x - 1]}${size - c.y + 1}`;
}

function App() {
  const [view, setView] = useState<View>('lobby');
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiColor, setAiColor] = useState<'black' | 'white'>(readLastAiColor);
  const [aiLevel, setAiLevel] = useState<'auto' | number>(readLastAiLevel);
  const [aiLevelSection, setAiLevelSection] = useState<'kyu' | 'dan'>(() => {
    const v = readLastAiLevel();
    return typeof v === 'number' && v >= 25 ? 'dan' : 'kyu';
  });
  const [problems, setProblems] = useState<any[]>([]);
  const [problemLoading, setProblemLoading] = useState(false);
  const [problemType, setProblemType] = useState<'all' | '吃子' | '死活' | '手筋'>('all');
  const [problemBoard, setProblemBoard] = useState<'all' | '13' | '19'>('all');
  const [judgeResult, setJudgeResult] = useState<{ achieved: boolean; goal: string; targets: number; deadCount: number } | null>(null);
  const [judgeLoading, setJudgeLoading] = useState(false);
  const [currentProblemId, setCurrentProblemId] = useState<string | null>(null);
  const [problemTryMode, setProblemTryMode] = useState(false);
  const [problemHint, setProblemHint] = useState('');
  const [tryBoard, setTryBoard] = useState<StoneColor[][] | null>(null);
  const [tryLastMove, setTryLastMove] = useState<Coordinate | null>(null);
  const [tryColor, setTryColor] = useState<StoneColor>(StoneColor.BLACK);
  const [tryMoves, setTryMoves] = useState<Array<{ x: number; y: number; color: StoneColor }>>([]);
  const tryGameRef = useRef<GoGame | null>(null);
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [authState, setAuthState] = useState<AuthState>('loading');
  const [me, setMe] = useState<UserInfo | null>(null);

  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [room, setRoom] = useState<RoomState | null>(null);
  const [clock, setClock] = useState<ClockState | null>(null);
  const [lastMove, setLastMove] = useState<Coordinate | null>(null);
  const [myRole, setMyRole] = useState<'host' | 'player' | 'spectator' | null>(null);
  const [cursor, setCursor] = useState<Coordinate | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth <= 900);
  const [soundOn, setSoundOn] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [deskInfoOpen, setDeskInfoOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [nickPromptOpen, setNickPromptOpen] = useState(false);
  const [nickEditMode, setNickEditMode] = useState(false);
  const [nickInput, setNickInput] = useState('');
  const [nickSaving, setNickSaving] = useState(false);
  const [nickError, setNickError] = useState('');
  const [countConfirmOpen, setCountConfirmOpen] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const tokenRef = useRef<string | null>(localStorage.getItem(TOKEN_KEY));
  const nicknameRef = useRef('');
  const roomCodeRef = useRef<string | null>(localStorage.getItem(ROOM_KEY));
  const prevBoardRef = useRef<number[][] | null>(null);
  const soundRef = useRef<SoundManager | null>(null);
  const soundOnRef = useRef(true);
  const prevMoveCountRef = useRef(0);
  const prevStoneCountRef = useRef(0);
  const prevClockMainRef = useRef<{ black: number | null; white: number | null }>({ black: null, white: null });

  const nickname = me?.nickname || '';

  useEffect(() => { nicknameRef.current = nickname; }, [nickname]);

  useEffect(() => { if (roomCode) roomCodeRef.current = roomCode; }, [roomCode]);

  // 恢复会话：凭本地 token 拉取本人信息
  useEffect(() => {
    const token = tokenRef.current;
    if (token) {
      fetch(SERVER_URL + '/api/me', { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((d) => {
          if (d.ok) {
            setMe(d.user);
            setAuthState('authenticated');
          } else {
            localStorage.removeItem(TOKEN_KEY);
            tokenRef.current = null;
            setAuthState('unauthenticated');
          }
        })
        .catch(() => setAuthState('unauthenticated'));
      return;
    }

    // 无 token：用记住的手机号自动登录（密码=手机号），手机/电脑一致
    let remembered = '';
    try { remembered = localStorage.getItem(REMEMBERED_USER_KEY) || ''; } catch { /* ignore */ }
    if (!/^1[3-9]\d{9}$/.test(remembered)) { setAuthState('unauthenticated'); return; }

    fetch(SERVER_URL + '/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: remembered, password: remembered }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (!d.ok) { setAuthState('unauthenticated'); return; }
        tokenRef.current = d.token;
        localStorage.setItem(TOKEN_KEY, d.token);
        setMe(d.user);
        setAuthState('authenticated');
        if (needsNickname(d.user)) {
          setNickEditMode(false);
          setNickInput('');
          setNickError('');
          setNickPromptOpen(true);
        }
        const s = socketRef.current;
        if (s && s.connected) {
          s.emit('auth', { token: d.token }, (r: any) => {
            if (r?.ok && roomCodeRef.current) {
              s.emit('reconnect', { roomCode: roomCodeRef.current }, (rr: any) => {
                if (!rr?.ok) {
                  localStorage.removeItem(ROOM_KEY);
                  roomCodeRef.current = null;
                }
              });
            }
          });
        }
      })
      .catch(() => setAuthState('unauthenticated'));
  }, []);

  // 建立 socket 连接
  useEffect(() => {
    const socket = io(SERVER_URL, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      setError('');
      const token = tokenRef.current;
      if (token) {
        socket.emit('auth', { token }, (res: any) => {
          if (!res?.ok) {
            localStorage.removeItem(TOKEN_KEY);
            tokenRef.current = null;
            setMe(null);
            setAuthState('unauthenticated');
            return;
          }
          // 若之前在房间内，凭账号重连
          if (roomCodeRef.current) {
            socket.emit('reconnect', { roomCode: roomCodeRef.current }, (r: any) => {
              if (!r?.ok) {
                alert('重连失败（房间不存在或已结束），已返回大厅');
                resetToLobby();
              }
            });
          }
        });
      }
    });

    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => setConnected(false));

    socket.on('roomState', (s: RoomState) => {
      setRoom(s);
      setRoomCode(s.code);
      setClock(s.clock);
      setLastMove(diffBoard(prevBoardRef.current, s.board));
      prevBoardRef.current = s.board;
      if (roomCodeRef.current === s.code && s.status !== 'waiting') setView('game');
      if (s.status === 'finished') {
        setTimeout(() => {
          const el = document.querySelector('.dg-scroll');
          if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
        }, 60);
      }
      // 落子/提子音效（手数增加时判定）
      const mc = s.moveCount ?? 0;
      const sc = countStones(s.board);
      if (mc > prevMoveCountRef.current && soundOnRef.current) {
        const captured = prevStoneCountRef.current + 1 - sc > 0;
        const lastColor = s.moveLog && s.moveLog.length > 0 ? s.moveLog[s.moveLog.length - 1].color : 'black';
        if (captured) soundRef.current?.playStoneCapture();
        else soundRef.current?.playStonePlace(lastColor === 'white' ? 'white' : 'black');
      }
      prevMoveCountRef.current = mc;
      prevStoneCountRef.current = sc;
      // 对局结束：刷新本人战绩（战绩在 finishGame 结算完才广播，此处拉到即最新）
      if (s.status === 'finished') {
        const token = tokenRef.current;
        if (token) {
          fetch(SERVER_URL + '/api/me', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((d) => { if (d.ok) setMe(d.user); })
            .catch(() => {});
        }
        // 等级变化提示（仅本人）
        if (s.rankChanges && nicknameRef.current) {
          const mySide = s.black?.nickname === nicknameRef.current ? 'black' : s.white?.nickname === nicknameRef.current ? 'white' : null;
          const mine = mySide ? s.rankChanges.find((rc) => rc.side === mySide) : null;
          if (mine) {
            const dir = mine.delta > 0 ? '升' : '降';
            alert(`等级变化：${mine.beforeLabel} → ${mine.afterLabel}（${dir} ${Math.abs(mine.delta)} 级，${mine.reason}）`);
          }
        }
      }
    });

    socket.on('moveRejected', (e: { error: string }) => {
      alert(`落子被拒: ${e.error}`);
    });

    socket.on('countRejected', (e: { error: string }) => {
      alert(e.error || '现在不能数子');
    });

    socket.on('countResponded', (d: { accepted: boolean }) => {
      if (!d.accepted) alert('已取消数子，继续下棋');
    });

    socket.on('undoRequested', () => {
      if (window.confirm('对方请求悔棋，是否同意？')) {
        socket.emit('respondUndo', { accept: true });
      } else {
        socket.emit('respondUndo', { accept: false });
      }
    });

    socket.on('undoResponded', (d: { accepted: boolean }) => {
      if (!d.accepted) alert('对方拒绝了悔棋请求');
    });

    socket.on('drawRequested', () => {
      if (window.confirm('对方请求和棋，是否同意？')) {
        socket.emit('respondDraw', { accept: true });
      } else {
        socket.emit('respondDraw', { accept: false });
      }
    });

    socket.on('drawResponded', (d: { accepted: boolean }) => {
      if (!d.accepted) alert('对方拒绝了和棋请求');
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 本地平滑倒计时：收到服务端 clock 快照后，每 250ms 递减当前方时间（服务端为权威，落子/结束时校准）
  useEffect(() => {
    if (!clock || room?.status !== 'playing' || room?.pendingCount) return;
    const timer = setInterval(() => {
      setClock((prev) => {
        if (!prev) return prev;
        const side = prev.current;
        const c = prev[side];
        if (c.main > 0) {
          return { ...prev, [side]: { ...c, main: Math.max(0, c.main - 0.25) } };
        }
        if (c.byoyomi > 0) {
          return { ...prev, [side]: { ...c, byoyomi: Math.max(0, c.byoyomi - 0.25) } };
        }
        return prev;
      });
    }, 250);
    return () => clearInterval(timer);
  }, [room?.status, !!clock, !!room?.pendingCount]);

  // 读秒开始提示音：某方基本时间用尽（main 归零）时播放一次
  useEffect(() => {
    if (!clock || room?.status !== 'playing') return;
    (['black', 'white'] as const).forEach((side) => {
      const m = clock[side].main;
      const prev = prevClockMainRef.current[side];
      if (prev != null && prev > 0 && m <= 0 && soundOnRef.current) {
        soundRef.current?.playByoyomi();
      }
      prevClockMainRef.current[side] = m;
    });
  }, [clock, room?.status]);
  // 音效：首次用户手势时初始化（浏览器要求手势解锁 AudioContext）
  useEffect(() => {
    const init = () => {
      if (!soundRef.current) {
        soundRef.current = new SoundManager({ enabled: true, volume: 0.7 });
        soundRef.current.initialize();
      }
    };
    window.addEventListener('pointerdown', init, { once: true });
    return () => window.removeEventListener('pointerdown', init);
  }, []);

  // 移动端判定（宽度 ≤ 900）
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 手机端：进入对局后给光标一个默认位置（天元）
  useEffect(() => {
    if (view === 'game' && isMobile && room?.status === 'playing' && myRole !== 'spectator' && !cursor) {
      const c = Math.floor(((room.boardSize as number) || 19) / 2) + 1;
      setCursor({ x: c, y: c });
    }
  }, [view, isMobile, room?.status, room?.boardSize, myRole, cursor]);

  // 移动端：出现结果 / 数子确认时自动展开信息面板（正常对局默认收起）
  useEffect(() => {
    if (room?.status === 'finished' || room?.pendingCount) setPanelOpen(true);
  }, [room?.status, room?.pendingCount]);

  // 返回大厅：清空身份，并断开重连获得全新 socket（清除旧房间成员身份）
  const resetToLobby = useCallback(() => {
    roomCodeRef.current = null;
    localStorage.removeItem(ROOM_KEY);
    setRoomCode(null);
    setView('lobby');
    setRoom(null);
    setClock(null);
    setLastMove(null);
    setMyRole(null);
    setCursor(null);
    setPanelOpen(false);
    setSheetOpen(false);
    prevClockMainRef.current = { black: null, white: null };
    prevBoardRef.current = null;
    setBusy(false);
    setError('');
    const s = socketRef.current;
    if (s) {
      s.disconnect();
      s.connect();
    }
  }, []);

  // 手动离开房间：对局中先确认，避免误触
  const confirmLeave = useCallback(() => {
    if (room?.status === 'playing' && !window.confirm('对局还没结束，离开后本局结束，确认离开吗？')) return;
    resetToLobby();
  }, [room?.status, resetToLobby]);

  const backToProblems = () => {
    resetToLobby();
    setView('problems');
  };

  // 选择预设头像（大厅账号栏弹层）：调接口 → 本地更新 me
  const chooseAvatar = async (idx: number) => {
    const token = tokenRef.current;
    if (!token || !me) return;
    setAvatarPickerOpen(false);
    try {
      const r = await changeAvatar(token, idx);
      if (r.ok && r.user) setMe(r.user);
      else setError(r.error || '头像设置失败');
    } catch {
      setError('头像设置失败');
    }
  };

  // 登录/注册成功
  const handleAuthed = useCallback((token: string, user: UserInfo) => {
    tokenRef.current = token;
    localStorage.setItem(TOKEN_KEY, token);
    roomCodeRef.current = null;
    localStorage.removeItem(ROOM_KEY);
    setMe(user);
    setAuthState('authenticated');
    setError('');
    if (needsNickname(user)) {
      setNickEditMode(false);
      setNickInput('');
      setNickError('');
      setNickPromptOpen(true);
    }
    const s = socketRef.current;
    if (s && s.connected) s.emit('auth', { token });
  }, []);

  // 退出登录
  const handleLogout = useCallback(async () => {
    const token = tokenRef.current;
    if (token) {
      try {
        await fetch(SERVER_URL + '/api/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* ignore */ }
    }
    localStorage.removeItem(TOKEN_KEY);
    try { localStorage.removeItem(REMEMBERED_USER_KEY); } catch { /* ignore */ }
    tokenRef.current = null;
    setMe(null);
    setAuthState('unauthenticated');
    resetToLobby();
  }, [resetToLobby]);

  const openNicknameEditor = () => {
    setNickEditMode(true);
    setNickInput(me?.nickname || '');
    setNickError('');
    setNickPromptOpen(true);
  };

  // 补昵称：注册后/老账号临时昵称登录后弹层保存
  const saveNickname = async () => {
    const token = tokenRef.current;
    if (!token) { setNickError('登录已失效，请重新登录'); return; }
    const name = nickInput.trim();
    if (!name) { setNickError('请输入昵称'); return; }
    if (name.length > 20) { setNickError('昵称最长 20 字'); return; }
    setNickSaving(true);
    setNickError('');
    try {
      const r = await changeNickname(token, name);
      if (!r.ok || !r.user) { setNickError(r.error || '保存失败，请重试'); return; }
      setMe(r.user);
      setNickPromptOpen(false);
      setNickEditMode(false);
      // 刷新 socket 里的用户快照，保证开局座位用新昵称
      const s = socketRef.current;
      if (s && s.connected) s.emit('auth', { token });
    } catch {
      setNickError('无法连接服务器，请稍后重试');
    } finally {
      setNickSaving(false);
    }
  };

  // 开始 AI 训练（本平台唯一对局入口）
  const startAITraining = () => {
    if (!me) { setError('尚未登录'); return; }
    setError('');

    const socket = socketRef.current;
    if (!socket) { setError('尚未连接服务器'); return; }

    setBusy(true);
    try {
      localStorage.setItem(LAST_LEVEL_KEY, String(aiLevel));
      localStorage.setItem(LAST_COLOR_KEY, aiColor);
    } catch { /* ignore */ }
    socket.emit('startAI', {
      boardSize: 'auto',
      color: aiColor,
      level: aiLevel,
      handicap: 0,
      komi: 7.5,
      timeControl: 'none',
    }, (res: any) => {
      setBusy(false);
      if (!res?.ok) { setError(res?.error || '发起 AI 对弈失败'); return; }
      roomCodeRef.current = res.roomCode;
      localStorage.setItem(ROOM_KEY, res.roomCode);
      setRoomCode(res.roomCode);
      setMyRole('player');
      setView('game');
    });
  };

  // 我的颜色 / 是否观战
  const myColor = room && nickname
    ? (room.black?.nickname === nickname ? 'black' : room.white?.nickname === nickname ? 'white' : null)
    : null;
  const isSpectator = myRole === 'spectator';
  const isPlaying = room?.status === 'playing';
  const isMyTurn = isPlaying && !isSpectator && (room?.problem ? true : room?.currentPlayer === myColor);
  const identityText = identityOf(myRole, myColor);

  // 联网动作
  const handleMove = (coord: Coordinate) => {
    if (problemTryMode) {
      const trial = tryGameRef.current;
      if (!trial) return;
      const color = trial.getCurrentPlayer();
      try { trial.playMove(coord, color); } catch { return; }
      setTryBoard(trial.getBoardState().stones.map((row) => [...row]));
      setTryLastMove(coord);
      setTryColor(trial.getCurrentPlayer());
      setTryMoves((prev) => [...prev, { x: coord.x, y: coord.y, color }]);
      return;
    }
    const socket = socketRef.current;
    if (!socket || !isMyTurn) return;
    socket.emit('move', { x: coord.x, y: coord.y });
  };

  // 手机端光标：方向键移动 + 确认落子（防误触）
  const bsNum = (room?.boardSize as number) || 19;
  const cursorEmpty = (c: Coordinate | null): boolean => {
    if (!c || !room?.board) return false;
    const row = room.board[c.y - 1];
    return !!row && row[c.x - 1] === StoneColor.EMPTY;
  };
  const moveCursor = (dx: number, dy: number) => {
    setCursor((prev) => {
      const base = prev || { x: Math.floor(bsNum / 2) + 1, y: Math.floor(bsNum / 2) + 1 };
      return {
        x: Math.min(bsNum, Math.max(1, base.x + dx)),
        y: Math.min(bsNum, Math.max(1, base.y + dy)),
      };
    });
  };
  const confirmMove = () => {
    const socket = socketRef.current;
    if (!socket || !isMyTurn || !cursor || !cursorEmpty(cursor)) return;
    socket.emit('move', { x: cursor.x, y: cursor.y });
  };
  const toggleSound = () => {
    setSoundOn((prev) => {
      const next = !prev;
      soundOnRef.current = next;
      if (next) {
        if (!soundRef.current) {
          soundRef.current = new SoundManager({ enabled: true, volume: 0.7 });
          soundRef.current.initialize();
        } else {
          soundRef.current.setEnabled(true);
        }
      } else {
        soundRef.current?.setEnabled(false);
      }
      return next;
    });
  };

  const handlePass = () => {
    const socket = socketRef.current;
    if (!socket || !isMyTurn) return;
    socket.emit('move', { x: null, y: null });
  };

  const handleResign = () => {
    if (!window.confirm('确定要认输吗？本局会判负。')) return;
    socketRef.current?.emit('resign');
  };

  const handleUndo = () => {
    if (room?.ai && !window.confirm('确认悔棋？会退回你和 AI 各一手。')) return;
    socketRef.current?.emit('requestUndo');
  };

  const handleDraw = () => {
    socketRef.current?.emit('requestDraw');
  };

  const minCountMoves = room?.countMinMoves ?? (room?.boardSize === 13 ? 50 : 80);
  const canCount = !!room && room.status === 'playing' && !room.pendingCount && room.moveCount >= minCountMoves && (!room.ai || isMyTurn);
  const filteredProblems = problems.filter((p) =>
    (problemType === 'all' || p.type === problemType) &&
    (problemBoard === 'all' || p.boardSize === Number(problemBoard))
  );
  const currentProblemIndex = currentProblemId ? filteredProblems.findIndex((p) => p.id === currentProblemId) : -1;
  const nextProblemId = currentProblemIndex >= 0 ? (filteredProblems[currentProblemIndex + 1]?.id ?? null) : null;
  const renderProblemGroup = (type: '吃子' | '死活' | '手筋', label: string) => {
    const list = filteredProblems.filter((p) => p.type === type);
    if (list.length === 0) return null;
    return (
      <>
        <h3 className="problems-group">{label}</h3>
        {list.map((p) => (
          <button key={p.id} className="problem-item" onClick={() => startProblem(p.id)}>
            <span className="problem-main">
              <span className="problem-title">{p.title}</span>
              {p.description && <span className="problem-desc">{p.description}</span>}
            </span>
            <span className="problem-meta">{p.boardSize}路</span>
          </button>
        ))}
      </>
    );
  };

  const handleCount = () => {
    if (!room) return;
    if (room.moveCount < minCountMoves) {
      alert(`满 ${minCountMoves} 手才能数子（当前 ${room.moveCount} 手）`);
      return;
    }
    if (!canCount) return;
    setCountConfirmOpen(true);
  };

  const confirmCount = () => {
    setCountConfirmOpen(false);
    socketRef.current?.emit('requestCount');
  };

  const settleDraw = () => {
    if (!window.confirm('确认和棋？本局将按和棋结束。')) return;
    socketRef.current?.emit('settleDraw');
  };

  const respondCount = (accept: boolean) => {
    socketRef.current?.emit('respondCount', { accept });
  };

  // 死活题：加载列表 / 开始做题 / 判定
  const loadProblems = async () => {
    setProblemLoading(true);
    try {
      const res = await fetch(SERVER_URL + '/api/problems');
      const data = await res.json();
      setProblems(Array.isArray(data) ? data : []);
    } catch {
      setProblems([]);
    }
    setProblemLoading(false);
  };

  const startProblem = (problemId: string) => {
    const socket = socketRef.current;
    if (!socket) { setError('尚未连接服务器'); return; }
    socket.emit('startProblem', { problemId }, (res: any) => {
      if (!res?.ok) { setError(res?.error || '开始做题失败'); return; }
      roomCodeRef.current = res.roomCode;
      localStorage.setItem(ROOM_KEY, res.roomCode);
      setRoomCode(res.roomCode);
      setMyRole('player');
      setCurrentProblemId(problemId);
      setJudgeLoading(false);
      setJudgeResult(null);
      setProblemTryMode(false);
      setProblemHint('');
      setTryBoard(null);
      setTryLastMove(null);
      setTryMoves([]);
      tryGameRef.current = null;
      setView('game');
    });
  };

  const handleJudge = (moves?: Array<{ x: number; y: number; color: StoneColor }>) => {
    const socket = socketRef.current;
    if (!socket || judgeLoading) return;
    setJudgeLoading(true);
    socket.emit('judgeProblem', moves && moves.length > 0 ? { moves } : {}, (res: any) => {
      setJudgeLoading(false);
      if (res?.ok) setJudgeResult(res);
      else alert(res?.error || '判定失败');
    });
  };

  const enterProblemTry = () => {
    if (!room?.problem) return;
    const board = (room.board as StoneColor[][]) || [];
    const setup: Array<{ color: StoneColor; x: number; y: number }> = [];
    for (let y = 0; y < board.length; y++) {
      for (let x = 0; x < (board[y]?.length || 0); x++) {
        const c = board[y][x];
        if (c !== StoneColor.EMPTY) setup.push({ color: c, x: x + 1, y: y + 1 });
      }
    }
    const trial = new GoGame({
      boardSize: room.boardSize as 9 | 13 | 19,
      handicap: 0,
      komi: 7.5,
      initialStones: setup,
      toMove: room.currentPlayer === 'white' ? StoneColor.WHITE : StoneColor.BLACK,
      timeSettings: { type: 'byoyomi', mainTime: 1800, byoyomiTime: 30, byoyomiPeriods: 3 },
    });
    trial.start();
    tryGameRef.current = trial;
    setTryBoard(board.map((row) => [...row]));
    setTryLastMove(lastMove);
    setTryColor(trial.getCurrentPlayer());
    setTryMoves([]);
    setProblemTryMode(true);
    setJudgeResult(null);
    setProblemHint('');
    socketRef.current?.emit('problemTrial', { active: true });
  };

  const exitProblemTry = () => {
    socketRef.current?.emit('problemTrial', { active: false });
    tryGameRef.current = null;
    setProblemTryMode(false);
    setTryBoard(null);
    setTryLastMove(null);
    setTryMoves([]);
  };

  const toggleProblemTry = () => {
    if (problemTryMode) exitProblemTry();
    else enterProblemTry();
  };

  const swapTryColor = () => {
    if (!problemTryMode || !tryBoard || tryMoves.length > 0) return;
    const setup: Array<{ color: StoneColor; x: number; y: number }> = [];
    for (let y = 0; y < tryBoard.length; y++) {
      for (let x = 0; x < (tryBoard[y]?.length || 0); x++) {
        const c = tryBoard[y][x];
        if (c !== StoneColor.EMPTY) setup.push({ color: c, x: x + 1, y: y + 1 });
      }
    }
    const next = tryColor === StoneColor.BLACK ? StoneColor.WHITE : StoneColor.BLACK;
    const trial = new GoGame({
      boardSize: (room?.boardSize as 9 | 13 | 19) || 19,
      handicap: 0,
      komi: 7.5,
      initialStones: setup,
      toMove: next,
      timeSettings: { type: 'byoyomi', mainTime: 1800, byoyomiTime: 30, byoyomiPeriods: 3 },
    });
    trial.start();
    tryGameRef.current = trial;
    setTryColor(next);
    setTryLastMove(null);
  };

  const submitProblem = () => {
    if (!room?.problem) return;
    if (problemTryMode) {
      if (tryMoves.length === 0) {
        setProblemHint('先在试下里走几步，再提交。');
        return;
      }
      handleJudge(tryMoves);
      return;
    }
    handleJudge();
  };

  const undoProblemMove = () => {
    if (problemTryMode) {
      const trial = tryGameRef.current;
      if (!trial || tryMoves.length === 0) return;
      trial.undo();
      const nextMoves = tryMoves.slice(0, -1);
      setTryMoves(nextMoves);
      setTryBoard(trial.getBoardState().stones.map((row) => [...row]));
      setTryColor(trial.getCurrentPlayer());
      const last = nextMoves[nextMoves.length - 1];
      setTryLastMove(last ? { x: last.x, y: last.y } : null);
      return;
    }
    handleUndo();
  };

  const redoProblemMove = () => {
    if (problemTryMode || !room?.problem?.canRedo) return;
    socketRef.current?.emit('problemRedo');
  };

  const showProblemHint = () => {
    if (!room?.problem) return;
    if (room.problem.practiceOnly || room.problem.type === '手筋') {
      setProblemHint('手筋题：先找对方的断点，再考虑自己的联络和先手。');
    } else if (room.problem.goal === 'live') {
      setProblemHint('先找眼位：能做真眼的地方优先，注意先手和劫材。');
    } else {
      setProblemHint('先找对方眼位和断点，第一手尽量是先手，让白棋做不出两只真眼。');
    }
  };

  // 状态文案
  const aiSeatColor = room?.black?.nickname === 'AI' ? 'black' : room?.white?.nickname === 'AI' ? 'white' : null;
  const aiThinking = !!(room?.ai && room.status === 'playing' && aiSeatColor && room.currentPlayer === aiSeatColor);
  const statusText = room
    ? (room.status === 'waiting'
        ? '等待对手加入...'
        : room.status === 'finished'
          ? `对局结束 - ${resultText(room.result)}`
          : aiThinking ? 'AI 思考中…' : '对局中')
    : '连接中...';

  const stones: StoneColor[][] = (room?.board as StoneColor[][]) || [];

  if (authState === 'loading') {
    return <div className="loading">🐣 加载中...</div>;
  }

  if (authState === 'unauthenticated') {
    return (
      <div className="app">
        <header className="app-header auth-brand">
          <img className="auth-brand-logo" src="/brand/logo-horizontal.png" alt="维实课堂" />
          <h1>AI训练平台</h1>
          <p className="subtitle">维实课堂专属 · 服务端权威引擎</p>
        </header>
        <main className="app-main app-main-lobby">
          <Auth onAuthed={handleAuthed} />
        </main>
        <footer className="app-footer">
          <p>维实课堂 · AI训练平台 © 2026</p>
        </footer>
      </div>
    );
  }

  // ===== 对局视图共享片段（桌面 / 移动端单一来源） =====
  const boardEl = (
    <GoBoard
      size={(room?.boardSize as Size) || 19}
      stones={problemTryMode && tryBoard ? tryBoard : stones}
      onMove={handleMove}
      disabled={problemTryMode ? false : !isMyTurn}
      showCoordinates={true}
      highlightLastMove={true}
      lastMove={problemTryMode ? tryLastMove : lastMove}
      showHover={problemTryMode ? true : (isMyTurn && !isMobile)}
      blockOccupied={true}
      cursor={isMobile && !problemTryMode ? cursor : null}
      cursorMode={isMobile && !problemTryMode}
      onCursor={setCursor}
      marks={(room?.problem?.marks as any) || []}
      fill={true}
      territory={room?.pendingCount?.result?.territory || null}
    />
  );

  const problemBanner = room?.problem ? (
    <div className="problem-banner">
      <span className="problem-banner-title">
        {room.problem.title}{room.problem.description ? ` · ${room.problem.description}` : ''}
      </span>
      <span className="problem-banner-goal">
        {room.problem.practiceOnly || room.problem.type === '手筋'
          ? '手筋练习'
          : room.problem.goal === 'live' ? '目标：做活' : '目标：杀棋'}
      </span>
    </div>
  ) : null;

  const captureBanner = room && room.captureRule > 0 && room.captures ? (
    <div className="capture-banner">
      <span className="capture-banner-rule">吃子棋：先吃满 {room.captureRule} 子胜</span>
      <span className="capture-banner-count">黑吃 {room.captures.black} : 白吃 {room.captures.white}</span>
    </div>
  ) : null;

  const countDialog = room?.pendingCount ? (
    <div className="pwd-mask">
      <div className="pwd-card count-dialog">
        <div className="pwd-title">数子结果</div>
        <div className="count-dialog-score">
          <span>黑 <b>{room.pendingCount.result.black}</b> 子</span>
          <span>白 <b>{room.pendingCount.result.white}</b> 子</span>
        </div>
        <div className="count-dialog-margin">
          {room.pendingCount.result.winner === 'black'
            ? `黑胜${room.pendingCount.result.margin != null ? ` ${room.pendingCount.result.margin} 子` : ''}`
            : room.pendingCount.result.winner === 'white'
              ? `白胜${room.pendingCount.result.margin != null ? ` ${room.pendingCount.result.margin} 子` : ''}`
              : '和棋'}
        </div>
        {room.pendingCount.result.dead && room.pendingCount.result.dead.length > 0 && (
          <div className="count-dead">死子 {room.pendingCount.result.dead.length} 个</div>
        )}
        <div className="count-legend">□ 方块 = 领地 ｜ ○ 红圈 = 死子</div>
        <p className="count-dialog-tip">认可结果即结束对局；不认可可以继续下。</p>
        <div className="count-dialog-actions">
          <button className="pwd-cancel" onClick={() => respondCount(false)}>继续下</button>
          {room.ai && <button className="pwd-draw" onClick={settleDraw}>和棋</button>}
          <button className="pwd-confirm" onClick={() => respondCount(true)}>认可结果</button>
        </div>
      </div>
    </div>
  ) : null;

  const infoPanel = (
    <div className="info-panel">
      <h3>对局信息</h3>
      <div className="info-grid">
        <div className="info-item">
          <span className="info-label">房间号:</span>
          <span className="info-value">{roomCode ?? '-'}</span>
        </div>
        <div className="info-item">
          <span className="info-label">棋盘:</span>
          <span className="info-value">{room?.boardSize ?? 19}路</span>
        </div>
        <div className="info-item">
          <span className="info-label">贴目:</span>
          <span className="info-value">7.5目</span>
        </div>
        <div className="info-item">
          <span className="info-label">手数:</span>
          <span className="info-value">{room?.moveCount ?? 0}</span>
        </div>
        <div className="info-item">
          <span className="info-label">身份:</span>
          <span className="info-value">{identityText}</span>
        </div>
        <div className="info-item">
          <span className="info-label">状态:</span>
          <span className="info-value">
            {room?.status === 'waiting' ? '等待' : room?.status === 'playing' ? '对局中' : '已结束'}
          </span>
        </div>
      </div>
    </div>
  );

  const spectatorPanel = (
    <div className="info-panel">
      <h3>观战者</h3>
      {room && room.spectators.length > 0 ? (
        <div className="spectator-list">
          {room.spectators.map((s, i) => (
            <span key={i} className="spectator-tag">{s}</span>
          ))}
        </div>
      ) : (
        <div className="empty-spectator">暂无观战者</div>
      )}
    </div>
  );

  const resultPanels = (
    <>
      {room?.result && (
        <div className="info-panel result-panel">
          <h3>对局结果</h3>
          <div className="result-text">{resultText(room.result)}</div>
          {room.result.blackScore != null && room.result.whiteScore != null && (
            <div className="result-score">
              黑 {room.result.blackScore} 子 : 白 {room.result.whiteScore} 子
            </div>
          )}
          <div className="result-reason">（{REASON_TEXT[room.result.reason] || room.result.reason}）</div>
          <div className="result-actions">
            {room.ai && !room.problem && (
              <button className="control-btn primary" onClick={startAITraining} disabled={busy || !connected}>
                {busy ? '准备中...' : '再来一盘'}
              </button>
            )}
            {room.problem && (
              <button className="control-btn primary" onClick={backToProblems}>返回死活训练</button>
            )}
            <button className="control-btn secondary" onClick={resetToLobby}>返回大厅</button>
          </div>
        </div>
      )}
      {judgeResult && (
        <div className="info-panel result-panel">
          <h3>判定结果</h3>
          <div className="result-text">{judgeResult.achieved ? '✅ 达成目标' : '❌ 未达成'}</div>
          <div className="result-reason">（目标{judgeResult.goal === 'live' ? '做活' : '杀棋'} · 目标{judgeResult.targets}子 · 死{judgeResult.deadCount}子）</div>
          <div className="result-actions">
            {!judgeResult.achieved && currentProblemId && (
              <button className="control-btn primary" onClick={() => startProblem(currentProblemId)}>再试一次</button>
            )}
            {judgeResult.achieved && nextProblemId && (
              <button className="control-btn primary" onClick={() => startProblem(nextProblemId)}>下一题</button>
            )}
            <button className="control-btn secondary" onClick={() => setJudgeResult(null)}>继续下</button>
            <button className="control-btn secondary" onClick={backToProblems}>题目列表</button>
          </div>
        </div>
      )}
    </>
  );

  // 移动端：贴棋盘下方的落子小工具条（压缩版）
  const movePad = isMobile && myColor ? (
    <div className="mg-pad">
      <button className="mg-pad-btn" onClick={() => moveCursor(-1, 0)} aria-label="左">◀</button>
      <button className="mg-pad-btn" onClick={() => moveCursor(0, -1)} aria-label="上">▲</button>
      <button className="mg-pad-btn" onClick={() => moveCursor(0, 1)} aria-label="下">▼</button>
      <button className="mg-pad-btn" onClick={() => moveCursor(1, 0)} aria-label="右">▶</button>
      <button className="mg-pad-confirm" onClick={confirmMove} disabled={!isMyTurn || !cursorEmpty(cursor)}>
        <span className="mg-pad-coord">{cursor ? coordLabel(cursor, bsNum) : '—'}</span>
        <span className="mg-pad-tx">{isMyTurn ? '落子' : '等待'}</span>
      </button>
    </div>
  ) : null;

  // ===== 移动端对局头（我方 / 手数·状态 / 对方，当前行棋方高亮） =====
  const renderMPlayer = (side: 'black' | 'white', seat: SeatInfo | null, isMe: boolean) => {
    const display = seat?.nickname || (side === 'black' ? '黑方' : '白方');
    const active = room?.status === 'playing' && room.currentPlayer === side;
    const cs = clock ? clock[side] : null;
    return (
      <div className={`mg-player ${active ? 'active' : ''}`}>
        <span className={`mg-avatar ${side}`}>{avatarGlyph(seat?.avatar, display)}</span>
        <span className="mg-pinfo">
          <span className="mg-pname">{display}{isMe && <em className="mg-me">我</em>}</span>
          <span className="mg-prank">{seat ? seatRankText(seat) : '—'}</span>
        </span>
        {cs && <span className={`mg-pclock ${cs.main <= 60 ? 'low' : ''}`}>{sideClockText(cs)}</span>}
      </div>
    );
  };

  // ===== 死活/手筋做题页：去掉对局壳子，只留题目、棋盘和做题按钮 =====
  const problem = room?.problem;
  const problemView = problem ? (
    <div className="problem-game">
      <div className="problem-topline">
        <button className="problem-back" onClick={backToProblems}>← 题目列表</button>
        <div className="problem-heading">
          <span className="problem-heading-title">{problem.title}</span>
          {problem.description && <span className="problem-heading-desc">{problem.description}</span>}
        </div>
        <span className="problem-goal-chip">
          {problem.practiceOnly || problem.type === '手筋'
            ? '手筋练习'
            : problem.goal === 'live' ? '目标：做活' : '目标：杀棋'}
        </span>
        {/白△/.test(problem.description || '') && (
          <span className="problem-legend">△ = 白棋关键子</span>
        )}
      </div>

      <div className="problem-board-wrap">{boardEl}</div>
      {movePad}

      <div className="problem-status">
        <span>
          {problemTryMode
            ? '试下中 · 自己摆棋，AI 不应手'
            : isMyTurn ? '轮到你' : isPlaying ? (aiThinking ? 'AI 思考中…' : '等待落子') : '本题结束'}
        </span>
        <span>{problemTryMode ? tryMoves.length : (room?.moveCount ?? 0)} 手</span>
      </div>

      {problemHint && <div className="problem-hint">{problemHint}</div>}

      {(judgeResult || room?.status === 'finished') && (
        <div className="problem-result-bar">
          <span className={judgeResult?.achieved ? 'ok' : ''}>
            {judgeResult ? (judgeResult.achieved ? '✅ 达成目标' : '❌ 未达成') : '本题结束'}
          </span>
          {currentProblemId && (
            <button onClick={() => startProblem(currentProblemId)}>再试一次</button>
          )}
          {nextProblemId && (
            <button onClick={() => startProblem(nextProblemId)}>下一题</button>
          )}
        </div>
      )}

      <div className="problem-actions">
        <button onClick={undoProblemMove} disabled={problemTryMode ? tryMoves.length === 0 : !room?.moveCount} title="上一步">←</button>
        <button onClick={redoProblemMove} disabled={problemTryMode || !room?.problem?.canRedo} title="下一步">→</button>
        {problemTryMode && (
          <button className="problem-color" onClick={swapTryColor} disabled={tryMoves.length > 0} title={tryMoves.length > 0 ? '先结束试下再换' : '切换黑白'}>
            {tryColor === StoneColor.BLACK ? '⚫ 黑' : '⚪ 白'}
          </button>
        )}
        <button className={problemTryMode ? 'primary' : ''} onClick={toggleProblemTry} disabled={!isPlaying}>
          {problemTryMode ? '结束试下' : '试下'}
        </button>
        {!problem.practiceOnly && (
          <button onClick={submitProblem} disabled={!isPlaying || judgeLoading}>
            {judgeLoading ? '提交中...' : '提交'}
          </button>
        )}
        <button onClick={showProblemHint}>提示</button>
        {problem.practiceOnly && nextProblemId && (
          <button onClick={() => startProblem(nextProblemId)}>下一题</button>
        )}
      </div>
    </div>
  ) : null;

  // ===== 桌面端对局卡片（真实头像 + 昵称 + 级位 + 时钟，当前行棋方高亮） =====
  const renderDPlayer = (side: 'black' | 'white') => {
    const seat = side === 'black' ? (room?.black ?? null) : (room?.white ?? null);
    const display = seat?.nickname || (side === 'black' ? '黑方' : '白方');
    const isMe = myColor === side;
    const active = room?.status === 'playing' && room?.currentPlayer === side;
    const cs = clock ? clock[side] : null;
    const playing = room?.status === 'playing';
    const lowTime = !!cs && playing && cs.main <= 60;
    return (
      <div className={`dg-card ${side} ${active ? 'active' : ''} ${isMe ? 'me' : ''}`}>
        <span className={`dg-avatar ${side}`}>{avatarGlyph(seat?.avatar, display)}</span>
        <span className="dg-pmeta">
          <span className="dg-pname">{display}{isMe && <em className="dg-me">我</em>}</span>
          <span className="dg-prank">{seat ? seatRankText(seat) : '—'}</span>
        </span>
        <span className={`dg-clock ${lowTime ? 'low' : ''}`}>{cs && playing ? sideClockText(cs) : '—'}</span>
      </div>
    );
  };

  const mgStatus = (
    <div className="mg-status">
      <span className={`mg-status-dot ${room?.status || ''}`} />
      <span className="mg-status-text">
        {room ? (room.status === 'playing' ? '对局中' : room.status === 'waiting' ? '等待中' : '已结束') : '连接中'}
      </span>
      <span className="mg-status-count">{room?.moveCount ?? 0}手</span>
    </div>
  );

  const mgTopbar = (
    <div className="mg-topbar">
      {myColor === 'white'
        ? renderMPlayer('white', room?.white ?? null, true)
        : renderMPlayer('black', room?.black ?? null, myColor === 'black')}
      {mgStatus}
      {myColor === 'white'
        ? renderMPlayer('black', room?.black ?? null, false)
        : renderMPlayer('white', room?.white ?? null, false)}
    </div>
  );

  return (
    <div className="app">
      {view !== 'review' && !(view === 'game' && room?.problem) && (
      <header className={view === 'game' ? (isMobile ? 'app-header app-header-game-mobile' : 'app-header app-header-game-desktop') : 'app-header lobby-header'}>
        {view === 'game' ? (
          isMobile ? mgTopbar : (
          <div className="game-topbar">
            <div className="gt-players">
              <span className="gt-player"><i className="gt-dot black" />{room?.black?.nickname || '黑方'}</span>
              <span className="gt-status">{statusText} · {room?.moveCount ?? 0}手</span>
              <span className="gt-player"><i className="gt-dot white" />{room?.white?.nickname || '白方'}</span>
            </div>
            <div className="gt-right">
              <span className="gt-room">房间 {roomCode ?? '-'}</span>
              <button className="control-btn ghost" onClick={confirmLeave}>🚪 离开</button>
            </div>
          </div>
          )
        ) : (
          <div className="lb-header-inner">
            <div className="lb-brand">
              <img className="lb-logo-img" src="/brand/logo-horizontal.png" alt="维实课堂" />
              <div className="lb-brand-text">
                <h1>AI训练平台</h1>
                <span className="lb-brand-sub">维实课堂专属 · 服务端权威引擎</span>
              </div>
            </div>

            <span className={`conn-badge conn-badge-head ${connected ? 'online' : 'offline'}`}>
              <span className="conn-dot"></span>
              <span className="conn-full">{connected ? '已连接服务器' : '连接断开 / 连接中...'}</span>
              <span className="conn-short">{connected ? '在线' : '离线'}</span>
            </span>

            <div className="user-bar lb-user">
              <span className="lb-avatar-wrap">
                <button
                  type="button"
                  className="lb-avatar lb-avatar-btn"
                  title="选择头像"
                  onClick={() => setAvatarPickerOpen((v) => !v)}
                >
                  {avatarGlyph(me?.avatar, nickname)}
                </button>
                {avatarPickerOpen && (
                  <div className="lb-avatar-pop">
                    <div className="lb-avatar-pop-title">选择头像</div>
                    <div className="lb-avatar-grid">
                      {AVATAR_EMOJIS.map((e, i) => (
                        <button
                          key={i}
                          type="button"
                          className={`lb-avatar-cell ${(me?.avatar ?? 0) === i ? 'active' : ''}`}
                          onClick={() => chooseAvatar(i)}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </span>
              <div className="lb-user-meta">
                <span className="user-name">
                  你好，{nickname}
                  {me && <span className="user-rank-chip">{me.rankLabel}</span>}
                  <button className="nick-edit-btn" title="修改昵称" onClick={openNicknameEditor}>✏️</button>
                </span>
                {me && (
                  <span className="user-stats">
                    {me.rankLabel} · {me.stats.wins}胜 {me.stats.losses}负 {me.stats.draws}和 · 胜率 {winRate(me.stats)}
                  </span>
                )}
              </div>
              <button className="logout-btn" onClick={handleLogout}>退出登录</button>
            </div>
          </div>
        )}
      </header>
      )}

      <main className={view === 'game' ? 'app-main app-main-game' : view === 'review' ? 'app-main app-main-review' : 'app-main app-main-lobby'}>
        {view === 'review' ? (
          <Review onBack={() => setView('lobby')} me={me?.nickname} />
        ) : view === 'problems' ? (
          <div className="lobby">
            <div className="lobby-card problems-card">
              <button className="control-btn ghost problems-back" onClick={() => setView('lobby')}>← 返回大厅</button>
              <h2>死活训练</h2>
              <p className="problems-sub">共 {problems.length} 题 · 吃子 {problems.filter((p) => p.type === '吃子').length} · 死活 {problems.filter((p) => p.type === '死活').length} · 手筋 {problems.filter((p) => p.type === '手筋').length}</p>
              {!problemLoading && problems.length === 0 && (
                <div className="review-empty">题库审核中，暂未开放。</div>
              )}
              <div className="problem-filters">
                <div className="problem-filter-row">
                  <span className="problem-filter-label">类型</span>
                  {(['all', '吃子', '死活', '手筋'] as const).map((t) => (
                    <button
                      key={t}
                      className={`problem-filter-chip ${problemType === t ? 'active' : ''}`}
                      onClick={() => setProblemType(t)}
                    >{t === 'all' ? '全部' : t}</button>
                  ))}
                </div>
                <div className="problem-filter-row">
                  <span className="problem-filter-label">棋盘</span>
                  {(['all', '13', '19'] as const).map((b) => (
                    <button
                      key={b}
                      className={`problem-filter-chip ${problemBoard === b ? 'active' : ''}`}
                      onClick={() => setProblemBoard(b)}
                    >{b === 'all' ? '全部' : `${b}路`}</button>
                  ))}
                  {(problemType !== 'all' || problemBoard !== 'all') && (
                    <button className="problem-filter-reset" onClick={() => { setProblemType('all'); setProblemBoard('all'); }}>清除</button>
                  )}
                </div>
              </div>
              <p className="problems-count">筛选后 {filteredProblems.length} 题</p>
              {problemLoading ? (
                <div className="problems-loading">加载中...</div>
              ) : filteredProblems.length === 0 ? (
                <div className="problems-empty">没有符合条件的题目，换个筛选试试。</div>
              ) : (
                <div className="problems-list">
                  {renderProblemGroup('吃子', '吃子训练')}
                  {renderProblemGroup('死活', '死活训练')}
                  {renderProblemGroup('手筋', '手筋训练')}
                </div>
              )}
            </div>
          </div>
        ) : view === 'lobby' ? (
          <div className="lobby lobby-view">
            <div className="lobby-card lb-card lb-train-card">
              {!aiPanelOpen && (
                <>
              <div className="lb-card-head">
                <h2>选择训练方式</h2>
                <p className="lb-card-sub">围空训练 · 13 路 / 19 路 · 服务端权威引擎</p>
              </div>

              <div className="lb-modes lb-modes-3">
                <button className="lb-mode" onClick={() => setView('review')}>
                  <span className="lb-mode-ic" aria-hidden>📚</span>
                  <span className="lb-mode-tx">复盘棋谱</span>
                  <span className="lb-mode-hint">回顾每一手</span>
                </button>
                <button
                  className="lb-mode"
                  onClick={() => { setView('problems'); loadProblems(); }}
                >
                  <span className="lb-mode-ic" aria-hidden>🧩</span>
                  <span className="lb-mode-tx">死活训练</span>
                  <span className="lb-mode-hint">吃子与死活</span>
                </button>
                <button
                  className={`lb-mode ${aiPanelOpen ? 'active' : ''}`}
                  onClick={() => { setAiPanelOpen((v) => !v); setError(''); }}
                >
                  <span className="lb-mode-ic" aria-hidden>🤖</span>
                  <span className="lb-mode-tx">AI 对弈</span>
                  <span className="lb-mode-hint">按级位对练</span>
                </button>
              </div>

              <p className="lb-guide">选 AI 对弈 → 选级别 → 开始；13路满50手、19路满80手才能数子</p>
                </>
              )}

              {aiPanelOpen && (
                <div className="lb-ai-head">
                  <button className="lb-ai-back" onClick={() => setAiPanelOpen(false)}>← 返回</button>
                  <span className="lb-ai-head-title">AI 对弈</span>
                </div>
              )}

              {aiPanelOpen && (
                <div className="lb-ai-setup">
                  <div className="lb-field">
                    <span className="lb-label">执子</span>
                    <div className="lb-seg">
                      <button
                        className={`lb-seg-btn ${aiColor === 'black' ? 'active' : ''}`}
                        onClick={() => setAiColor('black')}
                      >
                        执黑（先手）
                      </button>
                      <button
                        className={`lb-seg-btn ${aiColor === 'white' ? 'active' : ''}`}
                        onClick={() => setAiColor('white')}
                      >
                        执白（后手）
                      </button>
                    </div>
                  </div>

                  <div className="lb-field lb-field-col">
                    <span className="lb-label">难度</span>
                    <div className="lb-level">
                      <button
                        className={`lb-auto ${aiLevel === 'auto' ? 'active' : ''}`}
                        onClick={() => setAiLevel('auto')}
                      >
                        <span className="lb-auto-ic" aria-hidden>⚡</span>
                        <span className="lb-auto-tx">自动匹配</span>
                        <span className="lb-auto-sub">按我的等级{me?.rankLabel ? `（${me.rankLabel}）` : ''}</span>
                      </button>

                      <div className="lb-manual">
                        <div className="lb-seg lb-seg-sm">
                          <button
                            className={`lb-seg-btn ${aiLevelSection === 'kyu' ? 'active' : ''}`}
                            onClick={() => setAiLevelSection('kyu')}
                          >级位</button>
                          <button
                            className={`lb-seg-btn ${aiLevelSection === 'dan' ? 'active' : ''}`}
                            onClick={() => setAiLevelSection('dan')}
                          >段位</button>
                        </div>
                        <div className={`lb-level-chips ${aiLevelSection}`}>
                          {aiLevelSection === 'kyu' && <span className="lb-level-split">13路（15~10级）</span>}
                          {aiLevelSection === 'dan' && <span className="lb-level-split">19路（业余1~7段）</span>}
                          {(aiLevelSection === 'kyu' ? AI_KYU_CHIPS : AI_DAN_CHIPS).map((c) => (
                            <Fragment key={c.v}>
                              {aiLevelSection === 'kyu' && c.label === '9级' && (
                                <span className="lb-level-split">19路（9级~1级）</span>
                              )}
                              <button
                                className={`lb-chip ${aiLevel === c.v ? 'active' : ''}`}
                                onClick={() => setAiLevel(c.v)}
                              >{c.label}</button>
                            </Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {error && <div className="lobby-error">{error}</div>}

                  <button
                    className="lobby-submit lb-submit"
                    onClick={startAITraining}
                    disabled={busy || !connected}
                  >
                    {busy ? '准备中...' : '开始 AI 对弈'}
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : room?.problem ? (
          problemView
        ) : isMobile ? (
          <div className="mg-game">
            {mgTopbar}
            {problemBanner}
            {captureBanner}
            <div className="mg-board-wrap">{boardEl}</div>
            {movePad}
            <div className={`mg-collapse ${panelOpen ? 'open' : ''}`}>
              <button className="mg-collapse-head" onClick={() => setPanelOpen((v) => !v)}>
                <span className="mg-collapse-title">对局信息{room?.result ? ' · 结果' : ''}</span>
                <span className="mg-caret" aria-hidden>▾</span>
              </button>
              {panelOpen && (
                <div className="mg-collapse-body">
                  {resultPanels}
                  {infoPanel}
                  {room?.spectators?.length ? spectatorPanel : null}
                </div>
              )}
            </div>
            {room?.status === 'playing' && !room.problem && !room.pendingCount && (
              <div className={`mg-hint ${room.moveCount >= minCountMoves ? 'ok' : ''}`}>
                {room.moveCount >= minCountMoves
                  ? (aiThinking ? '等 AI 落子后可以数子' : `已满 ${minCountMoves} 手，可以数子了`)
                  : `全场还差 ${minCountMoves - room.moveCount} 手可数子`}
              </div>
            )}
            <div className="mg-iconbar">
              <button className="mg-ib danger" onClick={handleResign} disabled={!isPlaying || isSpectator}>
                <span className="mg-ib-ic">🏳️</span><span className="mg-ib-tx">认输</span>
              </button>
              <button className="mg-ib" onClick={handlePass} disabled={!isMyTurn}>
                <span className="mg-ib-ic">⏭️</span><span className="mg-ib-tx">停一手</span>
              </button>
              {room?.problem ? (
                !room.problem.practiceOnly && (
                  <button className="mg-ib" onClick={() => handleJudge()} disabled={!isPlaying || judgeLoading}>
                    <span className="mg-ib-ic">⚖️</span><span className="mg-ib-tx">{judgeLoading ? '判定中' : '判定'}</span>
                  </button>
                )
              ) : (
                <button className="mg-ib" onClick={handleCount} disabled={!canCount || isSpectator} title={room && room.moveCount < minCountMoves ? `满 ${minCountMoves} 手才能数子` : '数子'}>
                  <span className="mg-ib-ic">🧮</span><span className="mg-ib-tx">数子</span>
                </button>
              )}
              <button className="mg-ib" onClick={toggleSound}>
                <span className="mg-ib-ic">{soundOn ? '🔊' : '🔇'}</span><span className="mg-ib-tx">音效</span>
              </button>
              <button className="mg-ib" onClick={() => setSheetOpen(true)}>
                <span className="mg-ib-ic">⋯</span><span className="mg-ib-tx">更多</span>
              </button>
            </div>
            {sheetOpen && (
              <div className="mg-sheet-mask" onClick={() => setSheetOpen(false)}>
                <div className="mg-sheet" onClick={(e) => e.stopPropagation()}>
                  <div className="mg-sheet-bar" />
                  <button className="mg-sheet-item" disabled={!isMyTurn} onClick={() => { setSheetOpen(false); handleUndo(); }}>↩️ 悔棋</button>
                  {!room?.ai && (
                    <button className="mg-sheet-item" disabled={!isMyTurn} onClick={() => { setSheetOpen(false); handleDraw(); }}>🤝 和棋</button>
                  )}
                  <button className="mg-sheet-item danger" onClick={() => { setSheetOpen(false); confirmLeave(); }}>🚪 离开房间</button>
                  <button className="mg-sheet-item cancel" onClick={() => setSheetOpen(false)}>取消</button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="dg-game">
            <div className="dg-board-section">
              {problemBanner}
              {captureBanner}
              {boardEl}
            </div>

            <aside className="dg-panel">
              <div className="dg-players">
                {renderDPlayer('black')}
                <div className="dg-vs" aria-hidden><span>VS</span></div>
                {renderDPlayer('white')}
              </div>

              <div className="dg-scroll">
                {resultPanels}
                <div className="dg-info">
                  <button type="button" className="dg-info-head" onClick={() => setDeskInfoOpen((v) => !v)}>
                    <span>对局信息</span>
                    <span className={`dg-caret ${deskInfoOpen ? 'open' : ''}`} aria-hidden>▾</span>
                  </button>
                  {deskInfoOpen && <div className="dg-info-body">{infoPanel}</div>}
                </div>
                {room?.spectators?.length ? spectatorPanel : null}
              </div>

              {room?.status === 'playing' && !room.problem && !room.pendingCount && (
                <div className={`dg-hint ${room.moveCount >= minCountMoves ? 'ok' : ''}`}>
                  {room.moveCount >= minCountMoves
                    ? (aiThinking ? '等 AI 落子后可以数子' : `已满 ${minCountMoves} 手，可以数子了`)
                    : `全场还差 ${minCountMoves - room.moveCount} 手可数子`}
                </div>
              )}

              <div className="dg-actions">
                <button className="control-btn secondary" onClick={handleUndo} disabled={!isMyTurn}>↩️ 悔棋</button>
                <button className="control-btn secondary" onClick={handlePass} disabled={!isMyTurn}>⏭️ 停一手</button>
                {!room?.ai && (
                  <button className="control-btn secondary" onClick={handleDraw} disabled={!isMyTurn}>🤝 和棋</button>
                )}
                {!room?.problem && (
                  <button className="control-btn secondary" onClick={handleCount} disabled={!canCount || isSpectator} title={room && room.moveCount < minCountMoves ? `满 ${minCountMoves} 手才能数子` : '数子'}>🧮 数子</button>
                )}
                <button className={`control-btn ghost ${room?.problem ? '' : 'span2'}`} onClick={toggleSound}>{soundOn ? '🔊 音效' : '🔇 音效'}</button>
                {room?.problem && !room.problem.practiceOnly && (
                  <button className="control-btn primary" onClick={() => handleJudge()} disabled={!isPlaying || judgeLoading}>{judgeLoading ? '判定中...' : '判定'}</button>
                )}
                <button className="control-btn danger wide" onClick={handleResign} disabled={!isPlaying || isSpectator}>🏳️ 认输</button>
                <button className="control-btn ghost wide" onClick={confirmLeave}>🚪 离开房间</button>
              </div>
            </aside>
          </div>
        )}
      </main>

      {view !== 'game' && (
        <footer className="app-footer">
          <p>维实课堂 · AI训练平台 © 2026</p>
        </footer>
      )}

      {!connected && room && (
        <div className="conn-banner">连接断开，正在重连… 棋局会保留</div>
      )}

      {countDialog}

      {countConfirmOpen && (
        <div className="pwd-mask">
          <div className="pwd-card count-dialog">
            <div className="pwd-title">确认数子？</div>
            <p className="nick-sub">当前 {room?.moveCount ?? 0} 手，数子后会显示结果；不认可可以继续下。</p>
            <div className="pwd-actions">
              <button className="pwd-cancel" onClick={() => setCountConfirmOpen(false)}>取消</button>
              <button className="pwd-confirm" onClick={confirmCount}>确认数子</button>
            </div>
          </div>
        </div>
      )}

      {nickPromptOpen && (
        <div className="pwd-mask">
          <div className="pwd-card">
            <div className="pwd-title">{nickEditMode ? '修改昵称' : '给自己起个昵称'}</div>
            <p className="nick-sub">对局和战绩里会显示这个名字</p>
            <div className="auth-field">
              <label>昵称</label>
              <input
                type="text"
                value={nickInput}
                onChange={(e) => setNickInput(e.target.value)}
                maxLength={20}
                placeholder="例如：小明、棋棋"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && !nickSaving && saveNickname()}
              />
            </div>
            {nickError && <div className="auth-error">{nickError}</div>}
            <div className="pwd-actions">
              <button className="pwd-cancel" onClick={() => setNickPromptOpen(false)} disabled={nickSaving}>{nickEditMode ? '取消' : '稍后'}</button>
              <button className="pwd-confirm" onClick={saveNickname} disabled={nickSaving}>
                {nickSaving ? '保存中...' : '保存昵称'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

/** 身份文本 */
function identityOf(myRole: 'host' | 'player' | 'spectator' | null, myColor: 'black' | 'white' | null): string {
  if (myRole === 'spectator') return '观战';
  if (myColor === 'black') return '黑方';
  if (myColor === 'white') return '白方';
  if (myRole === 'host') return '房主';
  return '对局者';
}

/** 结果文本：黑胜 / 白胜 / 和棋，带比分差则附数字 */
function resultText(result: { winner: string; reason: string; margin?: number } | null): string {
  if (!result) return '';
  const name = result.winner === 'black' ? '黑胜' : result.winner === 'white' ? '白胜' : '和棋';
  if (result.margin != null && result.winner !== 'draw') {
    const m = Math.round(result.margin * 100) / 100;
    return `${name} ${m} 子`;
  }
  return name;
}

/** 秒数格式化：>=60s 显示 m:ss，否则读秒显示 N秒 */
function formatClockSeconds(sec: number): string {
  const s = Math.max(0, Math.ceil(sec));
  if (s >= 60) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  }
  return `${s}秒`;
}

/** 单方计时文本：基本时间用尽则显示读秒 + 剩余容错次数 */
function sideClockText(side: ClockSide): string {
  if (side.main > 0) return formatClockSeconds(side.main);
  return `读秒 ${Math.ceil(Math.max(0, side.byoyomi))}秒${side.periods >= 0 ? ` (剩${side.periods})` : ''}`;
}

/** 胜率：无对局返回 '—' */
function winRate(stats: { games: number; wins: number }): string {
  if (!stats.games) return '—';
  return `${Math.round((stats.wins / stats.games) * 100)}%`;
}

/** AI 手选档位（与后端 aiEngineFor 对齐：strength 10~31）
 *  级位 chips：s=10..24 → 15级 … 1级（由弱到强；15~10级走 13 路）
 *  段位 chips：s=25..31 → 业余1段 … 业余7段 */
const AI_KYU_CHIPS: { v: number; label: string }[] = Array.from({ length: 15 }, (_, i) => ({ v: 10 + i, label: rankLabel(10 + i) }));
const AI_DAN_CHIPS: { v: number; label: string }[] = Array.from({ length: 7 }, (_, i) => ({ v: 25 + i, label: rankLabel(25 + i) }));

export default App;
